import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { clubApi, fmtEur, statusLabel } from "../../lib/clubApi";
import { useClubStats } from "./ClubLayout";
import QuickSale from "./QuickSale";

// Simple search + list of soci (matches user's mockup).
export default function ClubMembers() {
    const { refresh } = useClubStats();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [saleFor, setSaleFor] = useState(null);

    const onSaved = (r) => {
        setMembers((list) => list.map((m) => (m.id === r.member.id ? r.member : m)));
        setSaleFor(null);
        refresh();
    };

    const load = async () => {
        setLoading(true);
        try {
            const data = await clubApi.listMembers({ limit: 1000 });
            setMembers(data);
        } catch {
            toast.error("Errore caricamento");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return members;
        return members.filter((m) =>
            (m.name || "").toLowerCase().includes(s) ||
            (m.phone || "").includes(s) ||
            (m.id || "").toLowerCase().includes(s) ||
            (m.zone || "").toLowerCase().includes(s)
        );
    }, [q, members]);

    return (
        <div className="space-y-4" data-testid="club-members-page">
            <h2 className="font-display font-bold text-2xl text-stone-900">Soci</h2>

            <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca nome, cognome, telefono o zona"
                className="input-club-new placeholder:text-stone-400"
                data-testid="members-search"
                autoComplete="off"
            />

            {loading ? (
                <div className="grid place-items-center py-10 text-stone-500"><Loader2 className="animate-spin" /></div>
            ) : filtered.length === 0 ? (
                <p className="text-stone-500 py-4">{q ? "Nessun socio trovato." : "Nessun socio."}</p>
            ) : (
                <ul className="space-y-2">
                    {filtered.map((m) => {
                        const st = statusLabel(m.status);
                        const open = saleFor === m.id;
                        return (
                            <li key={m.id} className={`rounded-xl border bg-white ${open ? "border-stone-900" : "border-stone-200"}`}>
                                <div className="flex items-center gap-2 px-3 py-3">
                                    <Link
                                        to={`/club/clienti/${m.id}`}
                                        className="min-w-0 flex-1 active:opacity-70"
                                        data-testid={`member-${m.id}`}
                                    >
                                        <p className="font-bold text-stone-900 truncate">{m.name}</p>
                                        <p className="text-xs text-stone-500 truncate">
                                            {m.phone}{m.zone ? ` · ${m.zone}` : ""}
                                        </p>
                                        <div className="flex gap-3 mt-1 text-xs text-stone-600">
                                            <span>👥 {m.visits_count} visite</span>
                                            <span>💶 {fmtEur(m.total_purchases)}</span>
                                        </div>
                                    </Link>
                                    <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full border ${st.cls}`}>
                                        {st.label}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setSaleFor(open ? null : m.id)}
                                        className={`shrink-0 w-10 h-10 rounded-full grid place-items-center active:scale-95 ${open ? "bg-stone-200 text-stone-900" : "bg-stone-900 text-white"}`}
                                        aria-label={open ? "Chiudi" : "Aggiungi vendita di oggi"}
                                        title="Aggiungi vendita di oggi"
                                        data-testid={`member-sale-toggle-${m.id}`}
                                    >
                                        {open ? <X size={18} /> : <Plus size={20} />}
                                    </button>
                                </div>
                                {open && (
                                    <div className="px-3 pb-3 pt-1 border-t border-stone-100">
                                        <p className="text-xs font-bold text-stone-700 mb-2">Vendita di oggi · {m.name}</p>
                                        <QuickSale member={m} onSaved={onSaved} autoFocus />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
